import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  IoLocationOutline,
  IoPersonOutline,
  IoMailOutline,
  IoPhonePortraitOutline,
  IoImageOutline,
  IoTimeOutline,
  IoCarOutline,
} from "react-icons/io5";
import { FaBox } from "react-icons/fa";
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import { useAuth } from "../../auth/AuthProvider";
import Api from "../../axiosConfig";
import { toast } from "react-toastify";
// Google Maps libraries
const libraries = ["places"];

const OrderDelivery = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  // Load Google Maps script
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries,
    version: "weekly",
  });

  const [formData, setFormData] = useState({
    senderPhone: user?.phone || "",
    pickupAddress: user?.address || "",
    recipientName: "",
    recipientPhone: "",
    recipientEmail: "",
    deliveryAddress: "",
    packageType: "document",
    packageSize: "small",
    requiredBy: "",
    specialInstructions: "",
    packageImage: null,
    deliveryType: "standard",
  });

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "file" ? files[0] : value,
    }));
  };

  // Autocomplete refs
  const pickupAutocompleteRef = useCallback((autocomplete) => {
    if (autocomplete !== null) {
      window.pickupAutocomplete = autocomplete;
    }
  }, []);

  const deliveryAutocompleteRef = useCallback((autocomplete) => {
    if (autocomplete !== null) {
      window.deliveryAutocomplete = autocomplete;
    }
  }, []);

  const handlePlaceSelect = (field) => {
    const autocomplete =
      field === "pickupAddress"
        ? window.pickupAutocomplete
        : window.deliveryAutocomplete;
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place.formatted_address) {
        setFormData((prev) => ({ ...prev, [field]: place.formatted_address }));
      }
    }
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.senderPhone && formData.pickupAddress;
      case 2:
        return (
          formData.recipientName &&
          formData.recipientPhone &&
          formData.deliveryAddress
        );
      case 3:
        return (
          formData.packageType && formData.packageSize && formData.deliveryType
        );
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => prev + 1);
    } else {
      toast.error("Please fill in all required fields");
    }
  };

  const prevStep = () => setCurrentStep((prev) => prev - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (currentStep !== 3) {
      nextStep();
      return;
    }

    if (!validateStep(3)) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const deliveryFormData = new FormData();

      // Add all form fields to FormData
      Object.keys(formData).forEach((key) => {
        if (key === "packageImage" && formData[key]) {
          deliveryFormData.append("file", formData[key]);
        } else if (formData[key] !== null && formData[key] !== undefined) {
          deliveryFormData.append(key, formData[key]);
        }
      });

      const response = await Api.post("/v1/deliveries", deliveryFormData, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      // Check if we got a delivery object back with an _id
      if (response.data && response.data._id) {
        toast.success("Delivery request submitted successfully!");
        // Add a small delay before navigation to ensure the toast is visible
        setTimeout(() => {
          navigate("/deliveries");
        }, 2000);
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      // Handle specific error messages from the backend
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Failed to submit delivery request. Please try again.";

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loadError) {
    return <div>Error loading maps</div>;
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white shadow-sm rounded-lg px-8 py-6">
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">
          Request a Delivery
        </h2>
        <form onSubmit={handleSubmit} className="space-y-8">
          {currentStep === 1 && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900 border-b pb-2">
                A. Sender Information
              </h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sender Phone
                  </label>
                  <input
                    type="tel"
                    name="senderPhone"
                    value={formData.senderPhone}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 border rounded-md shadow-sm focus:ring-primary-dark focus:border-primary-dark sm:text-sm"
                    placeholder="Enter sender phone number"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pickup Address
                  </label>
                  {isLoaded && (
                    <Autocomplete
                      onLoad={pickupAutocompleteRef}
                      onPlaceChanged={() => handlePlaceSelect("pickupAddress")}
                    >
                      <div className="relative">
                        <IoLocationOutline className="absolute left-3 top-3 text-gray-400" />
                        <input
                          type="text"
                          name="pickupAddress"
                          value={formData.pickupAddress}
                          onChange={handleChange}
                          className="block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:ring-primary-dark focus:border-primary-dark sm:text-sm"
                          required
                        />
                      </div>
                    </Autocomplete>
                  )}
                </div>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900 border-b pb-2">
                B. Recipient Information
              </h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <div className="relative">
                    <IoPersonOutline className="absolute left-3 top-3 text-gray-400" />
                    <input
                      type="text"
                      name="recipientName"
                      value={formData.recipientName}
                      onChange={handleChange}
                      className="block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:ring-primary-dark focus:border-primary-dark sm:text-sm"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <div className="relative">
                    <IoPhonePortraitOutline className="absolute left-3 top-3 text-gray-400" />
                    <input
                      type="tel"
                      name="recipientPhone"
                      value={formData.recipientPhone}
                      onChange={handleChange}
                      className="block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:ring-primary-dark focus:border-primary-dark sm:text-sm"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <div className="relative">
                    <IoMailOutline className="absolute left-3 top-3 text-gray-400" />
                    <input
                      type="email"
                      name="recipientEmail"
                      required
                      value={formData.recipientEmail}
                      onChange={handleChange}
                      className="block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:ring-primary-dark focus:border-primary-dark sm:text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Delivery Address
                  </label>
                  {isLoaded && (
                    <Autocomplete
                      onLoad={deliveryAutocompleteRef}
                      onPlaceChanged={() =>
                        handlePlaceSelect("deliveryAddress")
                      }
                    >
                      <div className="relative">
                        <IoLocationOutline className="absolute left-3 top-3 text-gray-400" />
                        <input
                          type="text"
                          name="deliveryAddress"
                          value={formData.deliveryAddress}
                          onChange={handleChange}
                          className="block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:ring-primary-dark focus:border-primary-dark sm:text-sm"
                          required
                        />
                      </div>
                    </Autocomplete>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Required By
                  </label>
                  <input
                    type="datetime-local"
                    name="requiredBy"
                    value={formData.requiredBy}
                    onChange={handleChange}
                    className="block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:ring-primary-dark focus:border-primary-dark sm:text-sm"
                    required
                  />
                </div>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900 border-b pb-2">
                C. Package Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Package Type
                  </label>
                  <div className="relative">
                    <FaBox className="absolute left-3 top-3 text-gray-400" />
                    <select
                      name="packageType"
                      value={formData.packageType}
                      onChange={handleChange}
                      className="block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:ring-primary-dark focus:border-primary-dark sm:text-sm"
                      required
                    >
                      <option value="DOCUMENT">DOCUMENT</option>
                      <option value="PARCEL">PARCEL</option>
                      <option value="MEDICAL_SUPPLIES">MEDICAL_SUPPLIES</option>
                      <option value="EQUIPMENT">EQUIPMENT</option>
                      <option value="MEDICATION">MEDICATION</option>
                      <option value="OTHER">OTHER</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Package Size
                  </label>
                  <div className="relative">
                    <FaBox className="absolute left-3 top-3 text-gray-400" />
                    <select
                      name="packageSize"
                      value={formData.packageSize}
                      onChange={handleChange}
                      className="block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:ring-primary-dark focus:border-primary-dark sm:text-sm"
                      required
                    >
                      <option value="small">Small (≤ 5kg)</option>
                      <option value="medium">Medium (5-20kg)</option>
                      <option value="large">Large (20-50kg)</option>
                      <option value="extra_large">
                        Extra Large ({">"}50kg)
                      </option>
                    </select>
                  </div>
                </div>

                {/* <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Package Image
                  </label>
                  <div className="relative">
                    <IoImageOutline className="absolute left-3 top-3 text-gray-400" />
                    <input
                      type="file"
                      name="packageImage"
                      onChange={handleChange}
                      accept="image/*"
                      className="block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:ring-primary-dark focus:border-primary-dark sm:text-sm"
                    />
                  </div>
                </div> */}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Delivery Type
                  </label>
                  <div className="relative">
                    <IoCarOutline className="absolute left-3 top-3 text-gray-400" />
                    <select
                      name="deliveryType"
                      value={formData.deliveryType}
                      onChange={handleChange}
                      className="block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:ring-primary-dark focus:border-primary-dark sm:text-sm"
                      required
                    >
                      <option value="STANDARD">STANDARD</option>
                      <option value="EXPRESS">EXPRESS</option>
                      <option value="SAME_DAY">SAME_DAY</option>
                      <option value="NEXT_DAY">NEXT_DAY</option>
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Special Instructions
                </label>
                <textarea
                  name="specialInstructions"
                  value={formData.specialInstructions}
                  onChange={handleChange}
                  rows={3}
                  className="block w-full px-3 py-2 border rounded-md shadow-sm focus:ring-primary-dark focus:border-primary-dark sm:text-sm"
                  placeholder="Any special handling instructions or delivery notes..."
                />
              </div>
            </div>
          )}

          <div className="flex justify-between mt-6">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={prevStep}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 border-solid rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-dark"
              >
                Previous
              </button>
            )}
            <button
              type="submit"
              disabled={loading}
              className="ml-auto px-4 py-2 text-sm font-medium text-white bg-primary-dark border border-transparent rounded-md shadow-sm hover:bg-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-dark disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </>
              ) : currentStep === 3 ? (
                "Submit Request"
              ) : (
                "Next"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrderDelivery;
